import 'ag-grid-enterprise/dist/styles/ag-grid.css';
import 'ag-grid-enterprise/dist/styles/ag-theme-material.css';
import 'ag-grid-enterprise';
import 'components/shared/SelectOption/LabeledSelect.scss';
import 'components/layouts/Account/AccountsGrid.scss';

import React, { Component, Fragment } from 'react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import {
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string
} from 'prop-types';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';

import Store from 'state/Store';
import Content from 'services/Content';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import { getHistoricalBalancesForDate } from 'services/Rest';
import { groupByIdMap } from 'services/Preferences';
import {
  DATE_DATA_FORMAT,
  DATE_HISTORICAL_START,
  isBetweenFromAndToDates,
} from 'services/Dates';
import { resetHistoricalBalances } from 'state/account/HistoricalBalanceActions';
import { getHiddenAccounts } from 'state/account/AccountSelectors';
import { getIsHistoricalBalancesLoading } from 'state/account/PositionsTableSelectors';
import { getCsvDownloadAccounts } from 'state/account/AccountsGridSelectors';
import { resetAccountsGridFilters } from 'state/account/AccountsGridFilteringActions';
import { PageHeader } from 'components/pages/Header/PageHeader';
import AsOfDateModal from 'components/shared/AsOfDate/AsOfDateModal';
import { ReactComponent as Customize } from 'assets/icons/customize.svg';
import PrintButton from 'components/shared/PrintButton/PrintButton';
import HeaderIcon from 'components/shared/HeaderIcon/HeaderIcon';

import AccountTypeFilter from 'components/layouts/Account/AccountTypeFilter';
import GridFilter from 'components/layouts/Account/GridFilter';
import SortAccountsFilter from 'components/layouts/Account/SortAccountsFilter';
import {
  filterAccountsGridAccountType,
  filterAccountsGridMobileColumn,
  filterAccountsGridPrintLayout,
  filterAccountsGridSorting
} from 'state/account/AccountsGridFilteringActions';
import {
  getRowClassRules,
  GridExportToCSV,
  chooseDefs,
  calculateRowHeight,
  formatterMapper,
  getContextMenuItems,
  processCellForClipboard
} from 'services/TableUtils';
import { PRINT_SETUP, PRINT_READY, PRINT_NONE } from 'services/PrintUtils';
import { ReactComponent as FilterIcon } from 'assets/icons/filterIcon.svg';
import { ReactComponent as SortIcon } from 'assets/icons/sort-icon.svg';
import { minInceptionDate } from 'services/Calculation';
import { downloadCSV } from 'services/Export/DownloadData';
import { csvFileNameFormat } from 'services/Formats';
import { getAccountsForExport } from 'services/Export/ExportAccounts';
import PRRDisclosure from './PRRDisclosure';
import { OWNERSHIP, sortBy } from 'services/TableSupport';
import { hasActiveAdvisory, hasActivePlus } from 'state/view/ViewSelectors';
import { getUpdatedAccountTypeFilteringOptions } from 'state/account/SelectAccountsFilteringReducer';
import { getPortfolioAsOfDate } from 'state/portfolio/PortfolioSelectors';
import {setDomLayoutEnabled} from 'services/FeatureFlags';
import BeaconDateInputWithSubmit from 'components/shared/Beacon/BeaconDateInputWithSubmit';

class AccountsGrid extends Component {
  state = {
    showDefaultSort: true,
    updateAccountTypeList: true,
    historicalBalanceDate: ''
  };

  componentDidUpdate(prevProps) {
    const { showDefaultSort, updateAccountTypeList } = this.state;

    // Update AccountType list if custom account group exists
    if (updateAccountTypeList) {
      this.updateAccountType();
    }

    if (showDefaultSort) {
      this.updateSortSelection();
    }

    if (this.gridApi) {
      // Resize columns on filter change/keep correct groups expanded
      if (prevProps.filters !== this.props.filters) {
        this.handleFilterChange(this.props.filters);
        this.gridApi.setColumnDefs(this.props.columnDefs);
        this.gridApi.sizeColumnsToFit();
      }

      if (this.props.isDesktopLayout !== prevProps.isDesktopLayout) {
        this.gridApi.redrawRows();
        this.gridApi.resetRowHeights();
      }

      if (this.props.columnDefs.length !== prevProps.columnDefs.length) {
        if (!isEmpty(this.props.historicalBalancesDate)) {
          this.gridApi.setColumnDefs(this.props.columnDefs);
          this.gridApi.sizeColumnsToFit();
        } else {
          this.gridApi.setColumnDefs(this.props.columnDefs);
          this.gridApi.sizeColumnsToFit();
        }
      }

      if (
        prevProps.printLayout !== PRINT_SETUP &&
        this.props.printLayout === PRINT_SETUP
      ) {
        if(setDomLayoutEnabled()) {
          this.gridApi.setDomLayout('print');
        }
        else {
          const agGridDiv = document.querySelector('.ag-theme-material');
          agGridDiv.classList.add('printer');
        }
        //setTimeout needed so grid redraws in time for print
        setTimeout(() => {
          Store.dispatch(filterAccountsGridPrintLayout(PRINT_READY));
        }, 2000);
      }

      if (
        prevProps.printLayout !== PRINT_READY &&
        this.props.printLayout === PRINT_READY
      ) {
        window.print();
        Store.dispatch(filterAccountsGridPrintLayout(PRINT_NONE));
      }

      if (
        this.props.printLayout === PRINT_NONE &&
        prevProps.printLayout === PRINT_READY
      ) {
        const agGridDiv = document.querySelector('.ag-theme-material');
        agGridDiv.classList.remove('printer');
        this.externalSortFilterChanged('desc');
        this.gridApi.setColumnDefs(this.props.columnDefs);
        this.gridApi.sizeColumnsToFit();
        this.gridApi.resetRowHeights();
      }
    }
  }

  componentWillUnmount() {
    Store.dispatch(resetAccountsGridFilters());
    Store.dispatch(resetHistoricalBalances());
  }

  updateAccountType = () => {
    const { filters, customAccountGroups } = this.props;
    if (customAccountGroups && customAccountGroups.length) {
      this.setState({ updateAccountTypeList: false });
      const customGroup = customAccountGroups.map((group, index) => ({
        label: group.groupName,
        accountDescriptor: group.accountDescriptors.map(
          (accountId) => accountId.accountId
        ),
        value: `custom${index + 1}`
      }));

      filters.accountType.options = getUpdatedAccountTypeFilteringOptions(
        filters.accountType.options,
        customGroup
      );
    }
  };

  updateSortSelection = () => {
    const { filters, defaultGroupBy } = this.props;
    if (
      filters &&
      filters.sorting &&
      defaultGroupBy &&
      defaultGroupBy.accountGroupingId
    ) {
      if (groupByIdMap[defaultGroupBy.accountGroupingId] === 'retirement') {
        this.setState({ showDefaultSort: false });
        return (filters.sorting.selection = 'desc');
      }
      if (groupByIdMap[defaultGroupBy.accountGroupingId] === 'nonRetirement') {
        this.setState({ showDefaultSort: false });
        return (filters.sorting.selection = 'asc');
      }
      if (groupByIdMap[defaultGroupBy.accountGroupingId] === 'product') {
        this.setState({ showDefaultSort: false });
        return (filters.sorting.selection = 'productType');
      }
      filters.sorting.selection =
        groupByIdMap[defaultGroupBy.accountGroupingId];
      this.setState({ showDefaultSort: false });
    }
  };

  postSort = (rowNodes) => {
    // Show Retirement accounts first if that filter is selected
    if (this.props.filters.sorting.selection === 'desc') {
      sortBy(rowNodes, 'desc');
    } else if (this.props.filters.sorting.selection === 'asc') {
      sortBy(rowNodes, 'asc');
    } else if (this.props.filters.sorting.selection === 'productType') {
      sortBy(rowNodes, 'productType');
    } else {
      sortBy(rowNodes, 'isOwner');
    }
  };

  getRowHeight = ({ node, data, api }) => {
    const { isDesktopLayout, printLayout } = this.props;
    if (node && node.group) {
      if (node.field === 'groupId') {
        if (printLayout !== PRINT_NONE) {
          return api.getSizesForCurrentTheme().rowHeight + 100;
        }
        return api.getSizesForCurrentTheme().rowHeight + 100;
      }
      //increase row height to prevent overlapping in print
      if (printLayout !== PRINT_NONE) {
        return (
          api.getSizesForCurrentTheme().rowHeight +
          (node.field === 'primaryOwner' ? node.key.length / 2 : 0) +
          45
        );
      }
      return (
        api.getSizesForCurrentTheme().rowHeight +
        (node.field === 'primaryOwner' ? node.key.length / 2 : 0)
      );
    } else if (data) {
      //increase row height to prevent overlapping in print
      if (printLayout !== PRINT_NONE) {
        return calculateRowHeight(data, api, isDesktopLayout) + 100;
      }
      return calculateRowHeight(data, api, isDesktopLayout) + 25;
    }
  };

  onGridReady = ({ api, columnApi }) => {
    this.gridApi = api;
    this.columnApi = columnApi;
    this.gridApi.sizeColumnsToFit();
  };

  gridSizeChanged = ({ api, columnApi }) => {
    this.gridApi = api;
    this.columnApi = columnApi;
    setTimeout(() => this.gridApi.sizeColumnsToFit(), 1000);
  };

  onFirstDataRendered = () => {
    // Ensure correct grouping order by ownership->registration sort is respected if chosen as default sort
    this.gridApi.sizeColumnsToFit();
    this.props.filters.sorting.selection === OWNERSHIP &&
      this.sortByRetirement();
  };

  handleFilterChange = () => {
    this.gridApi.getDisplayedRowCount() === 0
      ? this.gridApi.showNoRowsOverlay()
      : this.gridApi.hideOverlay();
  };

  externalAccountTypeFilterChanged = (newValue) => {
    Store.dispatch(filterAccountsGridAccountType(newValue));
  };

  mobileColumnFilterChanged = (newValue) => {
    Store.dispatch(filterAccountsGridMobileColumn(newValue));
  };

  externalSortFilterChanged = (newValue) => {
    this.setState({ showDefaultSort: false });
    Store.dispatch(filterAccountsGridSorting(newValue));

    if (newValue === 'asc' || newValue === 'desc') {
      this.sortByRetirement();
    }

    if (newValue === 'productType') {
      this.sortByRetirement();
    }

    // Keep order of owned -> related -> view only when sorting by ownership->registration
    if (newValue === OWNERSHIP) {
      this.sortByRetirement();
    }
  };

  isExternalFilterPresent = () => {
    return true;
  };

  doesExternalFilterPass = (node) => {
    const { accountTypeOptions, filters } = this.props;
    const { management, accountType } = filters;
    const managementFilterType = management.selection;
    const accountFilterType = accountType.selection;

    const isCustomFilter = accountFilterType.includes('custom');
    const filterCustomGroupData =
      isCustomFilter &&
      accountType.options.filter((opt) => opt.value === accountFilterType);
    const customGroupAccountIds = filterCustomGroupData.length
      ? filterCustomGroupData[0].accountDescriptor
      : [];
    const option =
      accountFilterType === 'retirement' ||
      accountFilterType === 'nonRetirement' ||
      accountFilterType === 'owned' ||
      accountFilterType === 'related' ||
      accountFilterType === 'viewOnly' ||
      isCustomFilter
        ? node.data
        : node.data.productType;

    return (
      !node.data.hiddenAccount &&
      (managementFilterType === 'all' ||
        (managementFilterType === 'advisory'
          ? node.data.managementType
          : !node.data.managementType)) &&
      (accountFilterType === 'all' ||
        (isCustomFilter && customGroupAccountIds.includes(option.accountId)) ||
        (!isCustomFilter && accountTypeOptions[accountFilterType](option)))
    );
  };

  sortByRetirement = () => {
    const sort = [
      {
        colId: 'ag-Grid-AutoColumn',
        sort: 'asc'
      }
    ];
    this.gridApi.setSortModel(sort);
  };

  getRowClass = ({ node }) => {
    const { group, field } = node;
    return getRowClassRules(group, field);
  };

  rowGroupCSVFormatter = ({ node }) => {
    return node.field === 'primaryOwner'
      ? node.key
      : formatterMapper(node.field, node.key);
  };

  handlePrint = () => {
    Store.dispatch(filterAccountsGridPrintLayout('setup'));
  };

  updateDate = (newDate) => {
    const today = moment().format(DATE_DATA_FORMAT);

    //if current date chosen, clear historical balances so all columns are shown
    if (isBetweenFromAndToDates(newDate, today, today)) {
      Store.dispatch(resetHistoricalBalances());
      this.setState({ historicalBalanceDate: '' });

      //otherwise pull in historical data alongside hiding unneeded column data
    }
    this.setState({ historicalBalanceDate: newDate });
    this.handleFilterChange('historical');
  };

  submitDate = (minDate) => {
    const {
      selections: { accountIds }
    } = this.props;
    const { historicalBalanceDate } = this.state;
    const today = moment().format(DATE_DATA_FORMAT);
    if (isBetweenFromAndToDates(historicalBalanceDate, minDate, today)) {
      getHistoricalBalancesForDate(
          moment.utc(historicalBalanceDate).format(DATE_DATA_FORMAT),
          accountIds
      );
      let datePicker = document.getElementById('custom-dates-beacon');
      datePicker.blur()
    }
  };

  exportGrid = () => {
    const { csvAccounts, historicalBalancesDate, rawAccountsPerformance } =
      this.props;

    downloadCSV(
      getAccountsForExport(
        csvAccounts,
        rawAccountsPerformance,
        historicalBalancesDate
      ),
      csvFileNameFormat('accounts', historicalBalancesDate)
    );
  };

  onCellKeydown = (params) => {
    if (!('colDef' in params)) {
      return;
    }
    if (!(params.event instanceof KeyboardEvent)) {
      return;
    }
    if (params.event.code !== 'Enter') {
      return;
    }
    if (params.colDef.showRowGroup) {
      params.node.setExpanded(!params.node.expanded);
    }
  }

  render() {
    const { showDefaultSort } = this.state;
    // Updates to default grouping on navigation from other page
    if (showDefaultSort) {
      this.updateSortSelection();
    }

    const {
      accountTypesWithoutHidden,
      accounts,
      customAccountGroups,
      columnDefs,
      columnDefsRetirementOrder,
      defaultColumnDef,
      autoGroupColumnDef,
      components,
      frameworkComponents,
      gridOptions,
      filters: {
        management,
        accountType,
        sorting,
        mobile: { selection: mobileSelection, options: mobileOptions }
      },
      has529Only,
      isMobileLayout,
      asOfDate,
      isDesktopLayout,
      isHistoricalBalancesLoading,
      showAdviceDisclosure,
      maxDate
    } = this.props;

    const managementFilterType = management.selection;
    const accountFilterType = accountType.selection;
    const sortFilterType = sorting.selection;
    const minDate = minInceptionDate(accounts)
      ? moment.utc(minInceptionDate(accounts)).format(DATE_DATA_FORMAT)
      : DATE_HISTORICAL_START;

    return (
      <Fragment>
        <div className="accounts-grid-container">
          <Fragment>
            {!isHistoricalBalancesLoading && (
              <div className="accounts-page-header">
                <PageHeader
                  title="Accounts"
                  className="accounts-page-top-header">
                  <HeaderIcon
                    to="/profile/customize-account-view"
                    internal={true}
                    icon={Customize}
                    classPrefix="accounts-page-header"
                    label="Customize"
                  />
                  <GridExportToCSV
                    buttonClass="header-button export-title"
                    onClick={this.exportGrid}
                  />
                  <PrintButton
                    className="accounts-page-header"
                    isDesktopLayout={isDesktopLayout}
                    handlePrint={this.handlePrint}
                  />
                </PageHeader>
                <div className="accounts-page-as-of-date-container">
                  <AsOfDateModal
                    asOfDate={asOfDate}
                    isDesktopLayout={isDesktopLayout}
                    containerClassName="accounts-page-as-of-date"
                  />
                </div>
              </div>
            )}
          </Fragment>
          <div className="grid-header grid-header-selectors">
            {!has529Only && (
              <Fragment>
                <div className="filter-select-group">
                  <AccountTypeFilter
                    label="Filter by Account Type"
                    change={this.externalAccountTypeFilterChanged}
                    managementType={managementFilterType}
                    accounts={accounts}
                    customAccountGroups={customAccountGroups}
                    accountTypes={accountTypesWithoutHidden}
                    filterState={accountType}
                  />
                  <FilterIcon />
                </div>
                <div className="filter-select-group">
                  <SortAccountsFilter
                    label="Sort Accounts by"
                    change={this.externalSortFilterChanged}
                    filterState={sorting}
                  />
                  <SortIcon />
                </div>
              </Fragment>
            )}
            <div className="filter-select-group historical-balances">
              <div className="historical-balances-label">
                <span className="select-label">View Historical Balances</span>
                <div className="aligning-disclousre-modal">
                  <DisclosureModal
                    className="position-filters-historical-balances-disclosure"
                    content={Content.historicalBalancesDisclosure}
                    title="Historical Balances"
                    hideLinkContent
                  />
                </div>
              </div>
              <BeaconDateInputWithSubmit
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={(newDate) => this.updateDate(newDate, minDate)}
                  onClear={() => Store.dispatch(resetHistoricalBalances())}
                  onSubmit={() => this.submitDate(minDate)} />
            </div>
            {isMobileLayout && (
              <div className="filter-select-group mobile">
                <GridFilter
                  label={'Account Data'}
                  options={mobileOptions.filter((a) => a != mobileSelection)}
                  change={this.mobileColumnFilterChanged}
                  selection={mobileSelection}
                />
              </div>
            )}
          </div>

          <PRRDisclosure showAdviceDisclosure={showAdviceDisclosure} />

          <div data-testid="accounts-ag-grid" className="ag-theme-material">
            <AgGridReact
              columnDefs={chooseDefs(
                columnDefs,
                columnDefsRetirementOrder,
                managementFilterType,
                accountFilterType,
                sortFilterType,
                has529Only
              )}
              defaultColDef={defaultColumnDef}
              defaultColGroupDef={defaultColumnDef}
              rowData={accounts}
              autoGroupColumnDef={autoGroupColumnDef}
              components={components}
              frameworkComponents={frameworkComponents}
              getRowClass={this.getRowClass}
              isExternalFilterPresent={this.isExternalFilterPresent}
              doesExternalFilterPass={this.doesExternalFilterPass}
              onGridReady={this.onGridReady}
              onGridSizeChanged={this.gridSizeChanged}
              onFirstDataRendered={this.onFirstDataRendered}
              onGridColumnsChanged={this.onGridColumnsChanged}
              getRowHeight={this.getRowHeight}
              postSort={this.postSort}
              processCellForClipboard={processCellForClipboard}
              getContextMenuItems={getContextMenuItems}
              onCellKeyDown={this.onCellKeydown}
              {...gridOptions}
            />
          </div>
        </div>
      </Fragment>
    );
  }
}

AccountsGrid.defaultProps = {
  accountTypeOptions: {},
  accountTypes: [],
  accountTypesWithoutHidden: [],
  customAccountGroups: [],
  accounts: [],
  autoGroupColumnDef: {},
  columnDefs: [],
  columnDefsRetirementOrder: [],
  columnTypes: {},
  components: {},
  defaultColumnDef: {},
  frameworkComponents: {},
  gridOptions: {},
  historicalBalances: []
};

AccountsGrid.propTypes = {
  accounts: arrayOf(
    shape({
      balance: number,
      dailyPriceChangeSum: oneOfType([number, string]),
      difference: number,
      hiddenAccount: bool.isRequired,
      isOwner: string.isRequired,
      managementType: bool,
      accountNameAndNumber: string.isRequired,
      performance: number,
      personalInvestment: string,
      priceChangePercentSum: number,
      priceSum: oneOfType([number, string]),
      primaryOwner: string,
      productType: number,
      quantitySum: oneOfType([number, string]),
      retirementAccount: bool.isRequired
    })
  ),
  columnDefs: arrayOf(
    shape({
      headerName: string,
      field: string.isRequired,
      rowGroup: bool,
      hide: bool,
      valueFormatter: func
    })
  ),
  defaultColumnDef: object.isRequired
};

const mapStateToProps = (state) => {
  const { balances, historicalBalances, view } = state;
  let historicalBalancesDate = '';
  if (historicalBalances) {
    historicalBalancesDate = historicalBalances.selectedDate;
  }
  return {
    isHistoricalBalancesLoading: getIsHistoricalBalancesLoading(state),
    isDesktopLayout: view.isDesktopLayout,
    asOfDate: historicalBalances.selectedDate || balances.asOfDate,
    csvAccounts: getCsvDownloadAccounts(state),
    hasBrokerage: view.hasBrokerage,
    hiddenAccounts: getHiddenAccounts(state),
    historicalBalancesDate,
    showAdviceDisclosure: hasActivePlus(state) || hasActiveAdvisory(state),
    maxDate: getPortfolioAsOfDate(state)
  };
};

export default connect(mapStateToProps)(AccountsGrid);